import { context, propagation, trace } from "@opentelemetry/api";

import type { WorkspaceFeedItemOldestEventsResponse } from "@storyboard/web-client";
import { getFeedItemOldestEvents } from "@storyboard/web-client";
import { transport } from "@/config";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import { bulkUpsertAccountEvent } from "@/models/repositories/AccountEvent";
import { idx, rowsByIndex } from "@/store/store";

const EVENTS_PER_PAGE = 10000;

export async function downloadOldestFeedItemEvents(
  itemIds: string[],
  workspaceId: string,
  timestamp?: string,
  deferEmit = false,
  maxPages?: number,
) {
  let page = 0;
  let count = 0;
  const parallelRequests = 1;
  while (true) {
    const promises: Promise<WorkspaceFeedItemOldestEventsResponse>[] = [];

    for (let index = 0; index < parallelRequests; index++) {
      const currentPage = page;
      promises.push(
        getFeedItemOldestEvents(transport, {
          workspaceId,
          itemIds,
          page: currentPage,
          pageSize: EVENTS_PER_PAGE,
          timestamp,
        }),
      );
      page += 1;
    }

    const responses = await Promise.all(promises);
    for (const response of responses) {
      if (!response) {
        continue;
      }
      await bulkUpsertAccountEvent(response.events);
    }
    const currentCount = responses.reduce((acc, r) => acc + r?.events.length, 0);
    count += currentCount;
    if (currentCount % EVENTS_PER_PAGE !== 0 || currentCount === 0) {
      return count;
    }

    if (maxPages && page >= maxPages) {
      return count;
    }

    if (page > 1000) {
      throw new Error("Too many pages");
    }
  }
}

const fetchLastTenItems = async (feedId: string): Promise<string[]> => {
  const tracer = trace.getTracer("bootstrap");

  const activeContext = propagation.extract(context.active(), {});
  const span = tracer.startSpan("fetchLastTenItems", {}, activeContext);

  const items = rowsByIndex("item", idx.itemsByFeed, feedId)
    .filter((item) => item.deletedAt == null)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    .slice(0, 10);
  span.end();
  return items?.map((item) => item.id) ?? [];
};

export const fetchFeedItemsAndDownloadEvents = async (feedId: string, workspaceId: string): Promise<number> => {
  const itemsMap = await fetchLastTenItems(feedId);
  if (itemsMap?.length > 0) {
    await downloadFeedItemContent(workspaceId, itemsMap);
  }
  return itemsMap?.length ?? 0;
};
